import {
  type FC,
  type HTMLAttributes,
  type ReactNode,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
  createContext,
  useContext,
} from "react";

/* ------------------------------------------------------------------ */
/*  Context – passes `responsive` flag to all compound children       */
/* ------------------------------------------------------------------ */

const ResponsiveCtx = createContext(false);

/* ------------------------------------------------------------------ */
/*  Prop types                                                        */
/* ------------------------------------------------------------------ */

interface DataTableProps extends TableHTMLAttributes<HTMLTableElement> {
  children: ReactNode;
  maxHeight?: string;
  /**
   * When true, the table tightens padding and text on small screens while
   * remaining a real `<table>`. The wrapper scrolls horizontally if the
   * content still overflows.
   *
   * @default false
   */
  responsive?: boolean;
}

interface RowGroupProps extends TableHTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

interface SubRowProps extends HTMLAttributes<HTMLTableRowElement> {
  children: ReactNode;
}

type CellProps = TdHTMLAttributes<HTMLTableCellElement>;

/* ------------------------------------------------------------------ */
/*  DataTable (root)                                                  */
/* ------------------------------------------------------------------ */

export const DataTable: FC<DataTableProps> & {
  Header: FC<TableHTMLAttributes<HTMLTableSectionElement>>;
  Body: FC<TableHTMLAttributes<HTMLTableSectionElement>>;
  RowGroup: FC<RowGroupProps>;
  Row: FC<HTMLAttributes<HTMLTableRowElement>>;
  SubRow: FC<SubRowProps>;
  HeaderCell: FC<ThHTMLAttributes<HTMLTableCellElement>>;
  Cell: FC<CellProps>;
} = ({ children, className = "", maxHeight, responsive = false, ...props }) => {
  const wrapperStyles = maxHeight
    ? { maxHeight, overflowY: "auto" as const }
    : {};
  const wrapperClass = maxHeight ? "custom-scrollbar" : "";

  return (
    <ResponsiveCtx.Provider value={responsive}>
      <div
        className={`overflow-x-auto border-2 border-slate-300 bg-white dark:border-slate-700 dark:bg-arti-background ${wrapperClass}`.trim()}
        style={wrapperStyles}
      >
        <table
          className={`w-full border-collapse text-left font-mono text-xs ${responsive ? "max-sm:text-[10px]" : ""} ${className}`.trim()}
          {...props}
        >
          {children}
        </table>
      </div>
    </ResponsiveCtx.Provider>
  );
};

/* ------------------------------------------------------------------ */
/*  Header                                                            */
/* ------------------------------------------------------------------ */

const Header: FC<TableHTMLAttributes<HTMLTableSectionElement>> = ({
  children,
  className = "",
  ...props
}) => (
  <thead
    className={`bg-slate-100 border-b-2 border-slate-300 dark:bg-arti-panel dark:border-slate-700 ${className}`.trim()}
    {...props}
  >
    {children}
  </thead>
);

/* ------------------------------------------------------------------ */
/*  Body                                                              */
/* ------------------------------------------------------------------ */

const Body: FC<TableHTMLAttributes<HTMLTableSectionElement>> = ({
  children,
  className = "",
  ...props
}) => (
  <tbody
    className={`divide-y divide-slate-200 bg-white dark:divide-slate-700 dark:bg-arti-background ${className}`.trim()}
    {...props}
  >
    {children}
  </tbody>
);

/* ------------------------------------------------------------------ */
/*  RowGroup                                                          */
/* ------------------------------------------------------------------ */

/**
 * RowGroup groups related rows into a visual cluster using a separate `<tbody>`.
 * Consecutive RowGroups are separated by a thicker border to distinguish groups.
 */
const RowGroup: FC<RowGroupProps> = ({
  children,
  className = "",
  ...props
}) => (
  <tbody
    className={`border-b-2 border-slate-300 last:border-b-0 dark:border-slate-700 [&>tr:not(:last-child)]:border-b [&>tr:not(:last-child)]:border-slate-200 dark:[&>tr:not(:last-child)]:border-slate-700 ${className}`.trim()}
    {...props}
  >
    {children}
  </tbody>
);

/* ------------------------------------------------------------------ */
/*  Row                                                               */
/* ------------------------------------------------------------------ */

const Row: FC<HTMLAttributes<HTMLTableRowElement>> = ({
  children,
  className = "",
  ...props
}) => (
  <tr
    className={`group relative transition-colors duration-200 hover:bg-slate-50 dark:hover:bg-arti-panel ${className}`.trim()}
    {...props}
  >
    {children}
  </tr>
);

/* ------------------------------------------------------------------ */
/*  SubRow                                                            */
/* ------------------------------------------------------------------ */

/**
 * SubRow renders a subordinate row within a RowGroup.
 * It has reduced padding and a muted hover to convey hierarchy.
 */
const SubRow: FC<SubRowProps> = ({ children, className = "", ...props }) => (
  <tr
    className={`group relative transition-colors duration-200 hover:bg-slate-50/60 dark:hover:bg-arti-panel/60 [&>td:first-child]:pl-8 [&>td]:py-2 ${className}`.trim()}
    {...props}
  >
    {children}
  </tr>
);

/* ------------------------------------------------------------------ */
/*  HeaderCell                                                        */
/* ------------------------------------------------------------------ */

const HeaderCell: FC<ThHTMLAttributes<HTMLTableCellElement>> = ({
  children,
  className = "",
  ...props
}) => {
  const responsive = useContext(ResponsiveCtx);
  return (
    <th
      className={`px-4 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-700 dark:text-slate-400 ${responsive ? "max-sm:px-2 max-sm:py-2 max-sm:text-[9px]" : ""} ${className}`}
      {...props}
    >
      {children}
    </th>
  );
};

/* ------------------------------------------------------------------ */
/*  Cell                                                              */
/* ------------------------------------------------------------------ */

const Cell: FC<CellProps> = ({ children, className = "", ...props }) => {
  const responsive = useContext(ResponsiveCtx);
  return (
    <td
      className={`px-4 py-3 align-middle text-slate-800 dark:text-slate-300 ${responsive ? "max-sm:px-2 max-sm:py-2" : ""} ${className}`.trim()}
      {...props}
    >
      {children}
    </td>
  );
};

/* ------------------------------------------------------------------ */
/*  Compound assignment                                               */
/* ------------------------------------------------------------------ */

DataTable.Header = Header;
DataTable.Body = Body;
DataTable.RowGroup = RowGroup;
DataTable.Row = Row;
DataTable.SubRow = SubRow;
DataTable.HeaderCell = HeaderCell;
DataTable.Cell = Cell;
